import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { MdFeedback } from "react-icons/md";
import { FiChevronDown } from "react-icons/fi";
import { FaClock, FaCopy, FaTrash, FaCog, FaQuestionCircle, FaCloud, FaEye, FaEdit, FaTimes, FaSearch } from "react-icons/fa";
import { TbReceipt } from "react-icons/tb";
import "./Bills.css";

// Date formatting utility functions
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const parseDDMMYYYYToDate = (dateString) => {
  if (!dateString) return "";

  try {
    const [day, month, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error parsing date:", error);
    return dateString;
  }
};

const normalizeAndValidateURL = (url) => {
  if (!url) {
    console.log("No URL provided");
    return null;
  }
  const baseUrl = 'https://backend.hira-tech.co.uk';
  const filename = url.split('/').pop();
  console.log(`Normalized filename: ${filename}, Original URL: ${url}`);
  return `${baseUrl}/api/bills/bills/${filename}`;
};

const BillsPage = () => {
  const [showPayDropdown, setShowPayDropdown] = useState(false);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showCreateBillPopup, setShowCreateBillPopup] = useState(false);
  const [showEditBillPopup, setShowEditBillPopup] = useState(false);
  const [tableData, setTableData] = useState([{ id: 1, rowNumber: "1", category: "General", description: "Initial Item", totalAmount: "" }]);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [bills, setBills] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterApplied, setFilterApplied] = useState(false);
  const [dateFilter, setDateFilter] = useState("All dates");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [amountOperator, setAmountOperator] = useState("Between");
  const payDropdownRef = useRef(null);
  const addDropdownRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const uploadPopupRef = useRef(null);
  const createPopupRef = useRef(null);
  const editPopupRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [billToDelete, setBillToDelete] = useState(null);
  const filterBtnRef = useRef(null);
  const filterModalRef = useRef(null);
  const [createBillFile, setCreateBillFile] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [filteredBills, setFilteredBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Get current date in DD-MM-YYYY format for display
  const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get current date in YYYY-MM-DD format for input fields
  const getCurrentDateForInput = () => {
    return new Date().toISOString().split('T')[0];
  };

  const [newBillData, setNewBillData] = useState({
    billNumber: "",
    supplier: "",
    date: getCurrentDate(),
    dueDate: getCurrentDate(),
    address: "",
    terms: "",
    memo: "",
  });

  const [editBillData, setEditBillData] = useState({
    billNumber: "",
    supplier: "",
    date: getCurrentDate(),
    dueDate: getCurrentDate(),
    amount: "",
    address: "",
    terms: "",
    memo: ""
  });

  const applyDateFilter = (date) => {
    const now = new Date();
    switch (date) {
      case "Since 365 Days ago":
        return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      case "This Month":
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      case "This Quarter":
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        return new Date(now.getFullYear(), quarterStartMonth, 1).toISOString().split('T')[0];
      case "This Year":
        return new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      case "Last Month":
        return new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      case "Last Quarter":
        const lastQuarterStartMonth = Math.floor((now.getMonth() - 3) / 3) * 3;
        return new Date(now.getFullYear(), lastQuarterStartMonth, 1).toISOString().split('T')[0];
      case "Custom":
        return customFromDate;
      default:
        return "";
    }
  };

  const applyToDateFilter = (date) => {
    const now = new Date();
    switch (date) {
      case "Since 365 Days ago":
      case "This Month":
      case "This Quarter":
      case "This Year":
        return now.toISOString().split('T')[0];
      case "Last Month":
        return new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
      case "Last Quarter":
        return new Date(now.getFullYear(), Math.floor((now.getMonth() - 3) / 3) * 3 + 2, 31).toISOString().split('T')[0];
      case "Last Year":
        return new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
      case "Custom":
        return customToDate;
      default:
        return "";
    }
  };

  const filterBills = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter !== "All dates") {
        const fromDate = applyDateFilter(dateFilter);
        const toDate = applyToDateFilter(dateFilter);
        if (fromDate) params.append("start_date", fromDate);
        if (toDate) params.append("end_date", toDate);
      }
      if (categoryFilter) {
        params.append("terms", categoryFilter);
      }
      const minVal = minAmount ? parseFloat(minAmount) : null;
      const maxVal = maxAmount ? parseFloat(maxAmount) : null;
      if (minVal !== null || maxVal !== null) {
        switch (amountOperator) {
          case "Between":
            if (minVal !== null) params.append("min_amount", minVal.toFixed(2));
            if (maxVal !== null) params.append("max_amount", maxVal.toFixed(2));
            break;
          case "Less than":
            if (minVal !== null) params.append("max_amount", minVal.toFixed(2));
            break;
          case "Greater than":
            if (minVal !== null) params.append("min_amount", minVal.toFixed(2));
            break;
          case "Equals":
            if (minVal !== null) {
              params.append("min_amount", minVal.toFixed(2));
              params.append("max_amount", minVal.toFixed(2));
            }
            break;
          default:
            break;
        }
      }
      console.log("Filter Parameters:", params.toString());
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to fetch filtered bills: ${response.status} ${response.statusText}\nResponse: ${text}`);
      }
      const data = await response.json();
      console.log("Filtered API Bills Response:", data);
      if (!Array.isArray(data)) {
        throw new Error("Unexpected response format: Expected an array of bills");
      }
      const mappedBills = data.map(bill => {
        const normalizedTerms = (bill.terms || "").trim().toLowerCase();
        return {
          id: bill.id,
          billNumber: bill.bill_number || "",
          supplier: bill.supplier || "",
          date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
          dueDate: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
          amount: bill.amount ? `£${(parseFloat(bill.amount)).toFixed(2)}` : "-",
          address: bill.address || "",
          terms: bill.terms || "",
          category: bill.terms ? (categoryMap[bill.terms] || bill.terms) : "Uncategorized",
          memo: bill.memo || "",
          status: bill.status || "Pending",
          fileName: bill.file_name || "Manual Entry",
          fileURL: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path}` : "",
          bill_pdf: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path.replace(/\\/g, '/')}` : null,
          attachments: bill.attachments || [],
          needsReview: bill.needs_review || false,
          isDeleted: bill.is_deleted || bill.deleted_from_supplier || false
        };
      });
      setBills(mappedBills);
      setFilterApplied(!!(dateFilter !== "All dates" || categoryFilter || minVal || maxVal));
      setError(null);
    } catch (error) {
      console.error("Error filtering bills:", error);
      setError(`Failed to filter bills: ${error.message}. Check console for details.`);
    }
  };

  useEffect(() => {
    const calculatedTotal = tableData.reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0);
    setTotalAmount(calculatedTotal);
  }, [tableData]);

  useEffect(() => {
    setFilteredBills(bills);
  }, [bills]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch("https://backend.hira-tech.co.uk/api/categories/", {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
        const data = await response.json();
        setCategories(data);
        const map = data.reduce((acc, cat) => {
          acc[cat.name] = cat.display_name || cat.name;
          return acc;
        }, {});
        setCategoryMap(map);
      } catch (error) {
        console.error(`Failed to fetch categories: ${error.message}`);
        setCategoryMap({ General: "General" });
        setCategories([{ id: 1, name: "General", display_name: "General" }]);
      }
    };

    const fetchBills = async () => {
      try {
        const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Failed to fetch bills: ${response.status} ${response.statusText}\nResponse: ${text.substring(0, 100)}...`);
        }
        const data = await response.json();
        console.log("Raw API Bills Response:", data);
        const mappedBills = data.map(bill => ({
          id: bill.id,
          billNumber: bill.bill_number || "",
          supplier: bill.supplier || "",
          date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
          dueDate: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
          amount: bill.amount ? `£${(parseFloat(bill.amount)).toFixed(2)}` : "-",
          address: bill.address || "",
          terms: bill.terms || "",
          category: bill.category || "Purchases",
          memo: bill.memo || "",
          status: bill.status || "Pending",
          fileName: bill.file_name || "Manual Entry",
          fileURL: bill.file_path || "",
          bill_pdf: bill.file_path || null,
          attachments: bill.attachments || [],
          needsReview: bill.needs_review || false,
          isDeleted: bill.is_deleted || bill.deleted_from_supplier || false
        }));
        console.log("Mapped Bills:", mappedBills);
        const filteredBills = mappedBills.filter(bill => bill.needsReview || bill.isDeleted);
        setAllBills(mappedBills);
        setBills(filteredBills);
        setError(null);
      } catch (error) {
        console.error("Error fetching bills:", error);
        setError(`Failed to load bills: ${error.message}`);
      }
    };

    const fetchSuppliers = async () => {
      try {
        const res = await fetch("https://backend.hira-tech.co.uk/api/suppliers/");
        if (!res.ok) throw new Error("Failed to fetch suppliers");
        const data = await res.json();
        setSuppliers(data);
      } catch (err) {
        console.error("Error loading suppliers:", err);
        setError(`Failed to load suppliers: ${err.message}`);
      }
    };

    fetchBills();
    fetchSuppliers();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (newBillData.supplier) {
      const selectedSupplier = suppliers.find((s) => s.id === parseInt(newBillData.supplier));
      if (selectedSupplier) {
        const supplierEmail = selectedSupplier.email || "";
        setNewBillData((prev) => ({ ...prev, address: supplierEmail }));
      } else {
        setNewBillData((prev) => ({ ...prev, address: "" }));
      }
    } else {
      setNewBillData((prev) => ({ ...prev, address: "" }));
    }
  }, [newBillData.supplier, suppliers]);

  // Handle date input changes - convert from DD-MM-YYYY to YYYY-MM-DD for form submission
  const handleDateChange = (field, value) => {
    setNewBillData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditDateChange = (field, value) => {
    setEditBillData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (payDropdownRef.current && !payDropdownRef.current.contains(e.target)) {
        setShowPayDropdown(false);
      }
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target)) {
        setShowAddDropdown(false);
      }
      if (batchDropdownRef.current && !batchDropdownRef.current.contains(e.target)) {
        setShowBatchDropdown(false);
      }
      if (uploadPopupRef.current && !uploadPopupRef.current.contains(e.target)) {
        setShowUploadPopup(false);
      }
      if (createPopupRef.current && !createPopupRef.current.contains(e.target)) {
        setShowCreateBillPopup(false);
      }
      if (editPopupRef.current && !editPopupRef.current.contains(e.target)) {
        setShowEditBillPopup(false);
        setSelectedBill(null);
      }
      if (filterModalRef.current && !filterModalRef.current.contains(e.target) && !filterBtnRef.current.contains(e.target)) {
        setShowFilterModal(false);
      }
      if (showDeleteConfirmation && !document.querySelector('.delete-confirmation-modal')?.contains(e.target)) {
        setShowDeleteConfirmation(false);
      }
    };
    if (showPayDropdown || showAddDropdown || showBatchDropdown || showUploadPopup || showCreateBillPopup || showEditBillPopup || showFilterModal || showDeleteConfirmation) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showPayDropdown, showAddDropdown, showBatchDropdown, showUploadPopup, showCreateBillPopup, showEditBillPopup, showFilterModal, showDeleteConfirmation]);

  useEffect(() => {
    if (showFilterModal && filterBtnRef.current && filterModalRef.current) {
      const dropdown = filterModalRef.current;
      const buttonRect = filterBtnRef.current.getBoundingClientRect();
      const dropdownRect = dropdown.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      dropdown.style.left = `${buttonRect.left}px`;
      if (buttonRect.left + dropdownRect.width > viewportWidth) {
        dropdown.style.left = `${viewportWidth - dropdownRect.width}px`;
      }
      dropdown.style.top = `${buttonRect.top + filterBtnRef.current.offsetHeight}px`;
    }
  }, [showFilterModal]);

  const handleUploadClick = () => {
    setShowUploadPopup(true);
  };

 const handleMakePayment = async (bill) => {
  try {
    const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/${bill.id}/pay/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to process payment: ${errorData.message || response.statusText}`);
    }
    const updatedBill = await response.json();
    const mappedBill = {
      id: updatedBill.id,
      billNumber: updatedBill.bill_number,
      supplier: updatedBill.supplier,
      date: formatDateToDDMMYYYY(updatedBill.date), // Format date to DD-MM-YYYY
      dueDate: formatDateToDDMMYYYY(updatedBill.due_date), // Format date to DD-MM-YYYY
      amount: updatedBill.amount ? `£${parseFloat(updatedBill.amount).toFixed(2)}` : "-",
      address: updatedBill.address || "",
      terms: updatedBill.terms || "",
      category: updatedBill.terms ? (categoryMap[updatedBill.terms] || updatedBill.terms) : "Uncategorized",
      memo: updatedBill.memo || "",
      status: updatedBill.status || "Paid",
      fileName: updatedBill.file_name || bill.fileName, // FIXED: Changed 'file.name' to 'bill.fileName'
      fileURL: updatedBill.file_path ? `https://backend.hira-tech.co.uk${updatedBill.file_path}` : "",
      bill_pdf: updatedBill.file_path ? `https://backend.hira-tech.co.uk${updatedBill.file_path.replace(/\\/g, '/')}` : null,
      attachments: bill.attachments || [],
      needsReview: updatedBill.needs_review || false,
      isDeleted: updatedBill.is_deleted || updatedBill.deleted_from_supplier || false,
    };
    setAllBills((prev) => prev.map((b) => (b.id === mappedBill.id ? mappedBill : b)));
    setBills((prev) => prev.filter((b) => b.id !== mappedBill.id));
    setShowCreateBillPopup(false);
    setSelectedBill(null);
    setNewBillData({
      billNumber: "",
      supplier: "",
      date: getCurrentDate(),
      dueDate: getCurrentDate(),
      address: "",
      terms: "",
      memo: "",
    });
    setTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: 0,
      },
    ]);
    alert("Payment processed successfully! Bill status updated to Paid.");
  } catch (error) {
    console.error("Error processing payment:", error);
    alert(`Error: ${error.message}`);
  }
};

  const handleCreateClick = () => {
    setNewBillData({
      billNumber: "",
      supplier: "",
      date: getCurrentDate(),
      dueDate: getCurrentDate(),
      address: "",
      terms: "",
      memo: "",
    });
    setTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: 0,
      },
    ]);
    setCreateBillFile(null);
    setSelectedBill(null);
    setIsEditing(false);
    setShowCreateBillPopup(true);
    console.log("handleCreateClick triggered, showCreateBillPopup:", showCreateBillPopup);
  };

  const handleCloudClick = (e) => {
    if (e.target.closest('.upload-button')) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredBills(bills);
      return;
    }
    const lowercasedTerm = term.toLowerCase();
    const filtered = bills.filter(bill => {
      const supplierName = getSupplierName(bill.supplier).toLowerCase();
      const billNumber = bill.billNumber.toLowerCase();
      const amount = bill.amount.toLowerCase();
      return (
        supplierName.includes(lowercasedTerm) ||
        billNumber.includes(lowercasedTerm) ||
        amount.includes(lowercasedTerm)
      );
    });
    setFilteredBills(filtered);
  };

  const handleFileChange = () => {
    const files = Array.from(fileInputRef.current.files);
    const invalidFiles = files.filter(file => !file.name.endsWith('.pdf'));
    if (invalidFiles.length > 0) {
      alert("Please select only PDF files.");
      setSelectedFile(null);
      fileInputRef.current.value = null;
      return;
    }
    setSelectedFile(files);
  };

  const handleViewBill = async (bill) => {
    try {
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/${bill.id}/`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch bill: ${errorData.message || response.statusText}`);
      }
      const billData = await response.json();
      setIsEditing(true);
      setSelectedBill(bill);
      setNewBillData({
        billNumber: billData.bill_number || bill.billNumber || "",
        supplier: billData.supplier || bill.supplier || "",
        date: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        address: billData.address || bill.address || "",
        terms: billData.terms || bill.terms || "",
        memo: billData.memo || bill.memo || "",
      });
      setTableData(
        billData.items || [
          {
            id: Date.now(),
            rowNumber: "1",
            category: billData.terms ? (categoryMap[billData.terms] || billData.terms) : "General",
            description: billData.memo || "Initial Item",
            totalAmount: billData.amount ? parseFloat(billData.amount) || 0 : 0,
          },
        ]
      );
      setCreateBillFile({ name: billData.file_name || bill.fileName || "No file" });
      setShowCreateBillPopup(true);
    } catch (error) {
      console.error("Error fetching bill:", error);
      alert(`Error: ${error.message}`);
    }
    console.log("handleViewBill triggered, bill:", bill, "showCreateBillPopup:", showCreateBillPopup);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedFile || selectedFile.length === 0) {
      setNotification({ type: 'error', message: 'Please select at least one file.' });
      return;
    }
    setIsProcessing(true);
    try {
      const uploadedBills = [];
      for (const file of selectedFile) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/upload/`, {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) {
          let errorMessage = 'Failed to upload bill';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || response.statusText;
          } catch (err) {
            console.error("Non-JSON response:", await response.text());
            errorMessage = 'Server returned an unexpected response';
          }
          throw new Error(errorMessage);
        }
        const { bill, extracted_data } = await response.json();
        const normalizedTerms = (bill.terms || "").trim().toLowerCase();
        const mappedBill = {
          id: bill.id,
          billNumber: bill.bill_number || "",
          supplier: bill.supplier || "",
          date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
          dueDate: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
          amount: bill.amount ? `£${parseFloat(bill.amount).toFixed(2)}` : "-",
          address: bill.address || "",
          terms: bill.terms || "",
          category: bill.terms ? (categoryMap[normalizedTerms] || bill.terms) : "",
          memo: bill.memo || "",
          status: bill.status || "Pending",
          fileName: bill.file_name || file.name,
          fileURL: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path}` : "",
          bill_pdf: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path.replace(/\\/g, '/')}` : null,
          attachments: bill.attachments || [],
          needsReview: bill.needs_review || true,
          isDeleted: bill.is_deleted || bill.deleted_from_supplier || false
        };
        uploadedBills.push(mappedBill);
      }
      setAllBills((prev) => [...prev, ...uploadedBills]);
      setBills((prev) => [...prev, ...uploadedBills]);
      setShowUploadPopup(false);
      setNotification({ type: 'success', message: `${uploadedBills.length} bill(s) uploaded successfully for review!` });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      console.error("Error uploading bill(s):", error);
      setNotification({ type: 'error', message: `Error: ${error.message}` });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateBillSubmit = async (e) => {
    e.preventDefault();
    if (!createBillFile && !isEditing) {
      alert("A PDF file is required to create a bill.");
      return;
    }
    if (!newBillData.supplier) {
      alert("Please select a supplier.");
      return;
    }
    const validCategory = categories.find(cat => cat.name === newBillData.terms);
    const termsValue = validCategory ? validCategory.name : (categories[0]?.name || "General");
    const formData = new FormData();
    if (!isEditing && createBillFile) formData.append("file", createBillFile);
    formData.append("bill_number", newBillData.billNumber || `BILL-${Date.now()}`);
    formData.append("supplier", parseInt(newBillData.supplier));

    // Convert DD-MM-YYYY to YYYY-MM-DD for API
    const apiDate = parseDDMMYYYYToDate(newBillData.date) || new Date().toISOString().split("T")[0];
    const apiDueDate = parseDDMMYYYYToDate(newBillData.dueDate) || new Date().toISOString().split("T")[0];

    formData.append("date", apiDate);
    formData.append("due_date", apiDueDate);
    formData.append("amount", tableData.reduce((sum, row) => sum + (parseFloat(row.totalAmount || "0") || 0), 0).toFixed(2));
    formData.append("address", newBillData.address || "");
    formData.append("terms", termsValue);
    formData.append("memo", newBillData.memo || "");
    formData.append("status", isEditing ? (selectedBill?.status || "Pending") : "Pending");
    formData.append("file_name", createBillFile?.name || (isEditing ? selectedBill?.fileName : "Manual Entry"));
    formData.append("needs_review", "false");
    formData.append("deleted_from_supplier", "false");
    console.log("FormData being sent:", Object.fromEntries(formData));
    try {
      const url = isEditing ? `https://backend.hira-tech.co.uk/api/bills/${selectedBill.id}/` : `https://backend.hira-tech.co.uk/api/bills/`;
      const method = isEditing ? "PUT" : "POST";
      const headers = isEditing ? { "Content-Type": "application/json" } : {};
      const body = isEditing ? JSON.stringify(Object.fromEntries(formData)) : formData;
      const response = await fetch(url, { method, headers, body });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to ${isEditing ? "update" : "create"} bill: ${errorData.message || JSON.stringify(errorData)}`);
      }
      const savedBill = await response.json();
      console.log("New bill API response:", savedBill);
      const mappedBill = {
        id: savedBill.id,
        billNumber: savedBill.bill_number,
        supplier: savedBill.supplier || parseInt(newBillData.supplier),
        date: formatDateToDDMMYYYY(savedBill.date), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(savedBill.due_date), // Format date to DD-MM-YYYY
        amount: `£${parseFloat(savedBill.amount).toFixed(2)}`,
        address: savedBill.address || "",
        terms: savedBill.terms || "",
        category: savedBill.category || (savedBill.terms ? (categoryMap[savedBill.terms] || savedBill.terms) : "Purchases"),
        memo: savedBill.memo || "",
        status: savedBill.status || "Pending",
        fileName: savedBill.file_name,
        fileURL: savedBill.file_path || "",
        bill_pdf: savedBill.file_path || null,
        attachments: savedBill.attachments || [],
        needsReview: savedBill.needs_review || false,
        isDeleted: savedBill.is_deleted || savedBill.deleted_from_supplier || false,
      };
      setAllBills(prev => (isEditing ? prev.map(b => b.id === mappedBill.id ? mappedBill : b) : [...prev, mappedBill]));
      setBills(prev => (isEditing ? prev.filter(b => b.id !== mappedBill.id || mappedBill.needsReview) : mappedBill.needsReview ? [...prev, mappedBill] : prev));
      setShowCreateBillPopup(false);
      setIsEditing(false);
      setSelectedBill(null);
      setNewBillData({ billNumber: "", supplier: "", date: getCurrentDate(), dueDate: getCurrentDate(), address: "", terms: "", memo: "" });
      setTableData([{ id: Date.now(), rowNumber: "1", category: "General", description: "Initial Item", totalAmount: 0 }]);
      setCreateBillFile(null);
      setAttachmentFiles([]);
      alert(`Bill ${isEditing ? "updated" : "created"} successfully!`);
    } catch (error) {
      console.error("Error creating/updating bill:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleView = async (bill) => {
    try {
      const response = await fetch(`${BASE_URL}/api/bills/${bill.id}/`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch bill: ${errorData.message || response.statusText}`);
      }
      const billData = await response.json();
      console.log("Bill API Response (handleView):", billData);
      const mappedBill = {
        id: billData.id,
        billNumber: billData.bill_number || "",
        supplier: billData.supplier || "",
        date: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        amount: billData.amount ? `£${parseFloat(billData.amount).toFixed(2)}` : "-",
        address: billData.address || "",
        terms: billData.terms || "",
        category: billData.terms ? (categoryMap[billData.terms] || billData.terms) : "Uncategorized",
        memo: billData.memo || "",
        status: billData.status || "Pending",
        fileName: billData.file_name || "Manual Entry",
        fileURL: billData.file_path ? `${BASE_URL}${billData.file_path}` : "",
        bill_pdf: billData.file_path ? `${BASE_URL}${billData.file_path.replace(/\\/g, '/')}` : null,
        attachments: billData.attachments || [],
        needsReview: billData.needs_review || false,
        isDeleted: billData.is_deleted || billData.deleted_from_supplier || false
      };
      console.log("Mapped Bill (handleView):", mappedBill);
      console.log("Bill PDF URL (handleView):", mappedBill.bill_pdf);
      setSelectedBill(mappedBill);
      setEditBillData({
        billNumber: billData.bill_number || "",
        supplier: billData.supplier || "",
        date: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        amount: billData.amount ? parseFloat(billData.amount).toFixed(2) : "",
        address: billData.address || "",
        terms: billData.terms || "",
        memo: billData.memo || ""
      });
      setIsEditing(false);
      setShowEditBillPopup(true);
    } catch (error) {
      console.error("Error fetching bill:", error);
      setNotification({ type: "error", message: `Error: ${error.message}` });
    }
  };

  const handleEdit = async (bill) => {
    try {
      const response = await fetch(`${BASE_URL}/api/bills/${bill.id}/`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to fetch bill: ${errorData.message || response.statusText}`);
      }
      const billData = await response.json();
      console.log("Bill API Response (handleEdit):", billData);
      const mappedBill = {
        id: billData.id,
        billNumber: billData.bill_number || "",
        supplier: billData.supplier || "",
        date: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        amount: billData.amount ? `£${parseFloat(billData.amount).toFixed(2)}` : "-",
        address: billData.address || "",
        terms: billData.terms || "",
        category: billData.terms ? (categoryMap[billData.terms] || billData.terms) : "Uncategorized",
        memo: billData.memo || "",
        status: billData.status || "Pending",
        fileName: billData.file_name || "Manual Entry",
        fileURL: billData.file_path ? `${BASE_URL}${billData.file_path}` : "",
        bill_pdf: billData.file_path ? `${BASE_URL}${billData.file_path.replace(/\\/g, '/')}` : null,
        attachments: billData.attachments || [],
        needsReview: billData.needs_review || false,
        isDeleted: billData.is_deleted || billData.deleted_from_supplier || false
      };
      console.log("Mapped Bill (handleEdit):", mappedBill);
      console.log("Bill PDF URL (handleEdit):", mappedBill.bill_pdf);
      setSelectedBill(mappedBill);
      setEditBillData({
        billNumber: billData.bill_number || "",
        supplier: billData.supplier || "",
        date: formatDateToDDMMYYYY(billData.date) || getCurrentDate(), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(billData.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        amount: billData.amount ? parseFloat(billData.amount).toFixed(2) : "",
        address: billData.address || "",
        terms: billData.terms || "",
        memo: billData.memo || ""
      });
      setIsEditing(true);
      setShowEditBillPopup(true);
    } catch (error) {
      console.error("Error fetching bill:", error);
      setNotification({ type: "error", message: `Error: ${error.message}` });
    }
  };

  const BASE_URL = "https://backend.hira-tech.co.uk";

  const handleEditBillSubmit = async (e) => {
    e.preventDefault();
    const validCategory = categories.find(cat => cat.name === editBillData.terms);
    const termsValue = validCategory ? validCategory.name : (categories[0]?.name || "General");

    // Convert DD-MM-YYYY to YYYY-MM-DD for API
    const apiDate = parseDDMMYYYYToDate(editBillData.date) || getCurrentDateForInput();
    const apiDueDate = parseDDMMYYYYToDate(editBillData.dueDate) || getCurrentDateForInput();

    const updatedBillData = {
      bill_number: editBillData.billNumber || `BILL-${Date.now()}`,
      supplier: editBillData.supplier ? parseInt(editBillData.supplier) : null,
      date: apiDate,
      due_date: apiDueDate,
      amount: editBillData.amount ? parseFloat(editBillData.amount) : 0,
      address: editBillData.address || "",
      terms: termsValue,
      memo: editBillData.memo || "",
      status: selectedBill.status || "Pending",
      file_name: selectedBill.fileName || "Manual Entry",
      file_path: selectedBill.fileURL ? selectedBill.fileURL.replace(`https://backend.hira-tech.co.uk`, '') : '',
      needs_review: false,
      deleted_from_supplier: false
    };
    console.log("UpdatedBillData being sent:", updatedBillData);
    try {
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/${selectedBill.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedBillData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update bill: ${errorData.message || JSON.stringify(errorData)}`);
      }
      const updatedBill = await response.json();
      const mappedBill = {
        id: updatedBill.id,
        billNumber: updatedBill.bill_number,
        supplier: updatedBill.supplier,
        date: formatDateToDDMMYYYY(updatedBill.date), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(updatedBill.due_date), // Format date to DD-MM-YYYY
        amount: updatedBill.amount ? `£${parseFloat(updatedBill.amount).toFixed(2)}` : "-",
        address: updatedBill.address || "",
        terms: updatedBill.terms || "",
        category: updatedBill.category || (updatedBill.terms ? (categoryMap[updatedBill.terms] || updatedBill.terms) : "Purchases"),
        memo: updatedBill.memo || "",
        status: updatedBill.status,
        fileName: updatedBill.file_name,
        fileURL: updatedBill.file_path || "",
        bill_pdf: updatedBill.file_path || null,
        attachments: selectedBill.attachments || [],
        needsReview: updatedBill.needs_review || false,
        isDeleted: updatedBill.is_deleted || updatedBill.deleted_from_supplier || false
      };
      setAllBills((prev) => prev.map((bill) => bill.id === mappedBill.id ? mappedBill : bill));
      setBills((prev) => prev.filter((bill) => bill.id !== mappedBill.id));
      setShowEditBillPopup(false);
      setSelectedBill(null);
      setEditBillData({
        billNumber: "",
        supplier: "",
        date: getCurrentDate(),
        dueDate: getCurrentDate(),
        amount: "",
        address: "",
        terms: "",
        memo: ""
      });
      setIsEditing(false);
      setNotification({ type: 'success', message: 'Bill updated successfully!' });
    } catch (error) {
      console.error("Error updating bill:", error);
      setNotification({ type: 'error', message: `Error: ${error.message}` });
    }
  };

  const handleAddRow = () => {
    setTableData([
      ...tableData,
      {
        id: Date.now(),
        rowNumber: (tableData.length + 1).toString(),
        category: "General",
        description: "New Item",
        totalAmount: 0,
      },
    ]);
  };

  const handleDeleteRow = (id) => {
    setTableData(
      tableData
        .filter((row) => row.id !== id)
        .map((row, index) => ({ ...row, rowNumber: (index + 1).toString() }))
    );
  };

  const handleCopyRow = (id) => {
    const rowToCopy = tableData.find((row) => row.id === id);
    if (rowToCopy) {
      setTableData([
        ...tableData,
        { ...rowToCopy, id: Date.now(), rowNumber: (tableData.length + 1).toString() },
      ]);
    }
  };

  const handleUpdateRow = (id, field, value) => {
    setTableData(
      tableData.map((row) =>
        row.id === id
          ? {
            ...row,
            [field]: value,
          }
          : row
      )
    );
  };

  const handleClearAllLines = () => {
    setTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: 0,
      },
    ]);
  };

  const handleAttachmentChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSizeMB = 20;
    const oversizedFiles = files.filter(file => file.size > maxSizeMB * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`The following files exceed the 20MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    setAttachmentFiles((prev) => [...prev, ...files]);
  };

  const getSupplierName = (id) => {
    const supplier = suppliers.find((s) => s.id === id);
    if (!supplier) return 'Unknown Supplier';
    return supplier.supplier_display_name || `${supplier.first_name || ''} ${supplier.last_name || ''}`.trim();
  };

  const handleClear = () => {
    setNewBillData({
      billNumber: "",
      supplier: "",
      date: getCurrentDate(),
      dueDate: getCurrentDate(),
      address: "",
      terms: "",
      memo: ""
    });
    setAttachmentFiles([]);
    setTableData([{ id: Date.now(), rowNumber: "1", category: "General", description: "Initial Item", totalAmount: "0.00" }]);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRecurring = () => {
    alert("Recurring bill creation is not implemented yet.");
  };

  const handleSaveAndNew = async (e) => {
    await handleCreateBillSubmit(e);
    setShowCreateBillPopup(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelectedBills = new Set(filteredBills.map(bill => bill.id));
      setSelectedBills(newSelectedBills);
    } else {
      setSelectedBills(new Set());
    }
  };

  const handleSelectBill = (billId) => {
    setSelectedBills(prev => {
      const newSet = new Set(prev);
      if (newSet.has(billId)) {
        newSet.delete(billId);
      } else {
        newSet.add(billId);
      }
      return newSet;
    });
  };

  const handleResetFilters = async () => {
    setDateFilter("All dates");
    setCategoryFilter("");
    setCustomFromDate("");
    setCustomToDate("");
    setMinAmount("");
    setMaxAmount("");
    setAmountOperator("Between");
    try {
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Failed to fetch all bills: ${response.status} ${response.statusText}\nResponse: ${text.substring(0, 100)}...`);
      }
      const data = await response.json();
      const mappedBills = data.map(bill => ({
        id: bill.id,
        billNumber: bill.bill_number || "",
        supplier: bill.supplier || "",
        date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
        amount: bill.amount ? `£${(parseFloat(bill.amount)).toFixed(2)}` : "-",
        address: bill.address || "",
        terms: bill.terms || "",
        category: bill.terms ? (categoryMap[bill.terms] || bill.terms) : "Uncategorized",
        memo: bill.memo || "",
        status: bill.status || "Pending",
        fileName: bill.file_name || "Manual Entry",
        fileURL: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path}` : "",
        bill_pdf: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path.replace(/\\/g, '/')}` : null,
        attachments: bill.attachments || []
      }));
      setBills(mappedBills);
      setFilterApplied(false);
    } catch (error) {
      console.error("Error resetting filters:", error);
      setError(`Failed to reset filters: ${error.message}`);
    }
  };

  const handleConfirmSelected = async () => {
    if (selectedBills.size === 0) return;
    try {
      const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/batch-confirm/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bill_ids: Array.from(selectedBills) }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to confirm selected bills: ${errorData.message || response.statusText}`);
      }
      const updatedBills = await response.json();
      const updatedMappedBills = updatedBills.map(bill => ({
        id: bill.id,
        billNumber: bill.bill_number,
        supplier: bill.supplier,
        date: formatDateToDDMMYYYY(bill.date), // Format date to DD-MM-YYYY
        dueDate: formatDateToDDMMYYYY(bill.due_date), // Format date to DD-MM-YYYY
        amount: bill.amount ? `£${parseFloat(bill.amount).toFixed(2)}` : "-",
        address: bill.address || "",
        terms: bill.terms || "",
        category: bill.terms ? (categoryMap[bill.terms] || bill.terms) : "Uncategorized",
        memo: bill.memo || "",
        status: bill.status || "Confirmed",
        fileName: bill.file_name || "Manual Entry",
        fileURL: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path}` : "",
        bill_pdf: bill.file_path ? `https://backend.hira-tech.co.uk${bill.file_path.replace(/\\/g, '/')}` : null,
        attachments: bill.attachments || []
      }));
      setAllBills(prev => prev.map(bill =>
        updatedMappedBills.find(u => u.id === bill.id) || bill
      ));
      setBills(prev => prev.map(bill =>
        updatedMappedBills.find(u => u.id === bill.id) || bill
      ));
      setSelectedBills(new Set());
      alert("Selected bills confirmed successfully!");
    } catch (error) {
      console.error("Error confirming bills:", error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedBills.size === 0) return;
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (billToDelete) {
        const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/${billToDelete}/`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to delete bill: ${errorData.message || response.statusText}`);
        }
        setAllBills((prev) => prev.filter((bill) => bill.id !== billToDelete));
        setBills((prev) => prev.filter((bill) => bill.id !== billToDelete));
        setSelectedBills((prev) => {
          const newSet = new Set(prev);
          newSet.delete(billToDelete);
          return newSet;
        });
        alert('Bill deleted successfully!');
      } else {
        const response = await fetch(`https://backend.hira-tech.co.uk/api/bills/batch-delete/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bill_ids: Array.from(selectedBills) }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to delete selected bills: ${errorData.message || response.statusText}`);
        }
        setAllBills((prev) => prev.filter((bill) => !selectedBills.has(bill.id)));
        setBills((prev) => prev.filter((bill) => !selectedBills.has(bill.id)));
        setSelectedBills(new Set());
        alert('Selected bills deleted successfully!');
      }
      setBillToDelete(null);
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error('Error deleting bill(s):', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <div className="bills-container">
      {notification && (
        <div
          className={`notification ${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '1rem',
            borderRadius: '4px',
            color: 'white',
            backgroundColor: notification.type === 'success' ? '#28a745' : '#dc3545',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          {notification.message}
        </div>
      )}
      {error && <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      <h1>Expenses</h1>
      <nav className="nav-bar">
        <NavLink to="/admin/pk-expenses" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Expenses</NavLink>
        <NavLink to="/admin/bills" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Bills</NavLink>
        <NavLink to="/admin/pk-suppliers" className={({ isActive }) => "nav-button" + (isActive ? " active" : "")}>Suppliers</NavLink>
      </nav>
      <div className="bills-header-row">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <p className="bills-title">Bills</p>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <NavLink to="/admin/add-bill" className="header-btn"><MdFeedback /> Give Feedback</NavLink>
            <div className="dropdowns-btn" onClick={() => setShowPayDropdown(!showPayDropdown)}>
              Pay bills <FiChevronDown />
              {showPayDropdown && (
                <div ref={payDropdownRef} className="dropdown-meu" style={{ width: "170px" }}>
                  <button>Make Recurring Bill</button>
                </div>
              )}
            </div>
            <div className="dropdown-button" onClick={() => setShowAddDropdown(!showAddDropdown)}>
              Add bill <FiChevronDown />
              {showAddDropdown && (
                <div ref={addDropdownRef} className="dropdown-meu" style={{ width: "200px", height: "123px" }}>
                  <button onClick={handleUploadClick}>Upload from computer</button>
                  <button onClick={handleCreateClick}>Create bill</button>
                  <button onClick={() => alert("Create recurring bill not implemented")}>Create recurring bill</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="billss-top-bar">
        <div className="bills-status-filters">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <button className="status-btn active">For review</button>
              <NavLink to="/admin/unpaid" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>Unpaid</NavLink>
              <NavLink to="/admin/paid" className={({ isActive }) => "status-btn" + (isActive ? " active" : "")}>Paid</NavLink>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <div className="dropdowns-btn" onClick={() => setShowBatchDropdown(!showBatchDropdown)}>
                <span className="batchs-actions">Batch actions</span> <FiChevronDown />
                {showBatchDropdown && (
                  <div ref={batchDropdownRef} className="dropdown-meu" style={{ width: "150px" }}>
                    <button
                      onClick={handleConfirmSelected}
                      disabled={selectedBills.size === 0}
                      style={{ opacity: selectedBills.size === 0 ? 0.5 : 1, cursor: selectedBills.size === 0 ? 'not-allowed' : 'pointer' }}
                    >
                      Confirm Selected
                    </button>
                    <button
                      onClick={() => {
                        setBillToDelete(null);
                        setShowDeleteConfirmation(true);
                      }}
                      disabled={selectedBills.size === 0}
                      style={{ opacity: selectedBills.size === 0 ? 0.5 : 1, cursor: selectedBills.size === 0 ? 'not-allowed' : 'pointer' }}
                    >
                      Delete Selected
                    </button>
                  </div>
                )}
              </div>
              <button className="filters-btn" ref={filterBtnRef} onClick={() => setShowFilterModal(!showFilterModal)}>
                Filter <FiChevronDown />
              </button>
              {filterApplied && (
                <div style={{ display: "flex", alignItems: "center", backgroundColor: "#e6f3ff", padding: "0.3rem 0.7rem", borderRadius: "4px" }}>
                  <span>Filter applied</span>
                  <button onClick={handleResetFilters} style={{ marginLeft: "0.5rem", color: "#d32f2f", border: "none", background: "none", cursor: "pointer" }}>
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bills-search-container" style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div className="search-bar" style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <FaSearch style={{ position: "absolute", left: "10px", color: "#666", zIndex: 1 }} />
            <input
              type="text"
              placeholder="Search by supplier, bill number, or amount..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                padding: "0.5rem 0.5rem 0.5rem 2rem",
                border: "1px solid #ddd",
                borderRadius: "4px",
                width: "300px",
                fontSize: "0.9rem"
              }}
            />
            {searchTerm && (
              <button
                onClick={() => handleSearch("")}
                style={{
                  position: "absolute",
                  right: "8px",
                  background: "none",
                  border: "none",
                  color: "#666",
                  cursor: "pointer"
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <button className="dropdown-btns">Export</button>
        </div>
      </div>
      {showFilterModal && filterBtnRef.current && (
        <div
          className="filters-dropdown"
          ref={filterModalRef}
          style={{
            position: "absolute",
            top: filterBtnRef.current.offsetTop + filterBtnRef.current.offsetHeight,
            left: filterBtnRef.current.offsetLeft,
            marginTop: "0",
          }}
        >
          <button className="dropdown-close-btn" onClick={() => setShowFilterModal(false)}>
            <FaTimes />
          </button>
          <div className="filter-options">
            <div className="filter-group">
              <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Dates</label>
              <div className="filter-input-row" style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
                <select
                  className="filter-select"
                  style={{ flex: "1", minWidth: "120px", padding: "0.5rem", fontSize: "0.9rem" }}
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="All dates">All dates</option>
                  <option value="Custom">Custom</option>
                  <option value="Since 365 Days ago">Since 365 Days ago</option>
                  <option value="This Month">This Month</option>
                  <option value="This Quarter">This Quarter</option>
                  <option value="This Year">This Year</option>
                  <option value="Last Month">Last Month</option>
                  <option value="Last Quarter">Last Quarter</option>
                  <option value="Last Year">Last Year</option>
                </select>
                {dateFilter === "Custom" && (
                  <>
                    <div style={{ flex: 1, minWidth: "120px" }}>
                      <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>From</label>
                      <input
                        type="date"
                        className="filter-input"
                        style={{ flex: "1", minWidth: "120px", width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                        value={customFromDate}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: "120px" }}>
                      <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>To</label>
                      <input
                        type="date"
                        className="filter-input"
                        style={{ flex: "1", minWidth: "120px", width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                        value={customToDate}
                        onChange={(e) => setCustomToDate(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="filter-group">
              <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Account/Category</label>
              <div className="filter-input-row" style={{ marginTop: "5px" }}>
                <select
                  className="filter-select"
                  style={{ width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.display_name || cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filter-group">
              <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Amount</label>
              <div className="filter-input-row" style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
                <select
                  className="filter-select"
                  style={{ flex: "1", minWidth: "120px", padding: "0.5rem", fontSize: "0.9rem" }}
                  value={amountOperator}
                  onChange={(e) => setAmountOperator(e.target.value)}
                >
                  <option value="Between">Between</option>
                  <option value="Less than">Less than</option>
                  <option value="Greater than">Greater than</option>
                  <option value="Equals">Equals</option>
                </select>
                {amountOperator === "Between" && (
                  <>
                    <div style={{ flex: 1, minWidth: "120px" }}>
                      <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Minimum</label>
                      <input
                        type="number"
                        className="filter-input"
                        style={{ flex: "1", minWidth: "120px", width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        placeholder="Minimum (£)"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: "120px" }}>
                      <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Maximum</label>
                      <input
                        type="number"
                        className="filter-input"
                        style={{ flex: "1", minWidth: "120px", width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        placeholder="Maximum (£)"
                      />
                    </div>
                  </>
                )}
                {(amountOperator === "Less than" || amountOperator === "Greater than" || amountOperator === "Equals") && (
                  <div style={{ flex: 1, minWidth: "120px" }}>
                    <label style={{ fontSize: "0.9rem", marginBottom: "0.2rem", display: "block" }}>Value</label>
                    <input
                      type="number"
                      className="filter-input"
                      style={{ flex: "1", minWidth: "120px", width: "100%", padding: "0.5rem", fontSize: "0.9rem" }}
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                      placeholder="Value (£)"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="filter-actions" style={{ textAlign: "right", marginTop: "15px" }}>
              <button className="filter-btn apply-btn" onClick={() => { filterBills(); setShowFilterModal(false); }}>Apply</button>
              <button className="filter-btn reset-btn" onClick={handleResetFilters}>Reset</button>
            </div>
          </div>
        </div>
      )}
      {showUploadPopup && (
        <div className="upload-popup-overlay">
          <div className="upload-popup" ref={uploadPopupRef}>
            <button
              className="close-button"
              onClick={() => {
                setShowUploadPopup(false);
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = null;
                }
              }}
            >
              ×
            </button>
            <h2>Upload a bill from your computer</h2>
            <div className="upload-area" onClick={handleCloudClick} style={{ cursor: "pointer" }}>
              <FaCloud size={50} style={{ marginBottom: "1rem", color: "#808080" }} />
              <p>Click to select file</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf"
                multiple
                style={{ display: "none" }}
              />
              {selectedFile && selectedFile.length > 0 && (
                <p style={{ margin: "1rem 0", color: "#333" }}>
                  Selected: {selectedFile.map(file => file.name).join(', ')}
                </p>
              )}
              <button
                className="upload-button"
                onClick={handleFileUpload}
                disabled={isProcessing || !selectedFile}
              >
                {isProcessing ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateBillPopup && (
        <div className="upload-popup-overlay">
          <div className="create-bill-popup" ref={createPopupRef}>
            <div className="bill-form-header">
              <div className="header-title">
                <FaClock className="header-icon" />
                <span>{isEditing ? "Edit Bill" : "Bill"}</span>
              </div>
              <div className="header-actions">
                <button type="button" className="icon-btn" onClick={() => alert("Copy bill not implemented")}>
                  <FaCopy />
                  <span>Copy</span>
                </button>
                <button type="button" className="text-icon-btn">
                  <MdFeedback />
                  <span>Give Feedback</span>
                </button>
                <button type="button" className="icon-btn">
                  <FaQuestionCircle />
                </button>
                <button
                  className="close-button"
                  type="button"
                  onClick={() => {
                    setShowCreateBillPopup(false);
                    setIsEditing(false);
                    setSelectedBill(null);
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <h2 className="form-main-title">{isEditing ? "Edit Bill" : "Bill"}</h2>
            <form onSubmit={handleCreateBillSubmit} className="bill-form-container">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group wide-group">
                    <label>Supplier</label>
                    <select
                      value={newBillData.supplier}
                      onChange={(e) => setNewBillData({ ...newBillData, supplier: e.target.value })}
                      style={{ width: "300px" }}
                    >
                      <option value="">Select a supplier</option>
                      {suppliers.length === 0 ? (
                        <option disabled>No suppliers found 😵</option>
                      ) : (
                        suppliers.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.supplier_display_name ||
                              `${supplier.first_name} ${supplier.last_name}`}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="form-group amount-display">
                    <label>Balance Due</label>
                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "25px" }}>
                      £{totalAmount.toFixed(2)}
                    </p>
                    {selectedBill && selectedBill.status !== "Paid" && isEditing && (
                      <button
                        type="button"
                        className="make-payment"
                        onClick={() => handleMakePayment(selectedBill)}
                        style={{
                          marginTop: "0.5rem",
                          padding: "0.5rem 1rem",
                          backgroundColor: "#004aad",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        Make Payment
                      </button>
                    )}
                  </div>
                </div>
                <div className="form-row five-items-row">
                  <div className="form-group">
                    <label>Mailing address</label>
                    <input
                      type="text"
                      placeholder="Enter mailing address"
                      value={newBillData.address}
                      onChange={(e) => setNewBillData({ ...newBillData, address: e.target.value })}
                      style={{ width: "200px" }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-80px" }}>
                    <label>Terms</label>
                    <select
                      value={newBillData.terms}
                      onChange={(e) => {
                        const selectedCategory = e.target.value;
                        setNewBillData({ ...newBillData, terms: selectedCategory });
                        setTableData(tableData.map(row => ({ ...row, category: selectedCategory })));
                      }}
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.display_name || cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-160px" }}>
                    <label>Bill Date</label>
                    <input
                      type="text"
                      placeholder="DD-MM-YYYY"
                      value={newBillData.date}
                      onChange={(e) => handleDateChange("date", e.target.value)}
                      style={{ width: "200px" }}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, width: "200px", marginLeft: "-240px" }}>
                    <label>Due Date</label>
                    <input
                      type="text"
                      placeholder="DD-MM-YYYY"
                      value={newBillData.dueDate}
                      onChange={(e) => handleDateChange("dueDate", e.target.value)}
                      style={{ width: "200px" }}
                    />
                  </div>
                  <div className="form-group" style={{ width: "200px", marginLeft: "-320px" }}>
                    <label>Bill number</label>
                    <input
                      type="text"
                      value={newBillData.billNumber}
                      onChange={(e) => setNewBillData({ ...newBillData, billNumber: e.target.value })}
                      style={{ width: "200px" }}
                    />
                  </div>
                </div>
                <div className="table-section">
                  <div className="table-header">
                    <span>Items</span>
                    <button
                      type="button"
                      className="settings-btn"
                      onClick={() => alert("Settings not implemented yet")}
                    >
                      <FaCog />
                    </button>
                  </div>
                  {isProcessing && (
                    <div
                      style={{
                        backgroundColor: '#e6f3ff',
                        padding: '0.5rem',
                        textAlign: 'center',
                        borderRadius: '4px',
                        marginBottom: '1rem',
                        color: '#004aad',
                        fontWeight: 'bold',
                      }}
                    >
                      Processing bill upload and extracting data...
                    </div>
                  )}
                  <div className="table-container">
                    <table className="item-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Amount</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row) => (
                          <tr key={row.id}>
                            <td>{row.rowNumber}</td>
                            <td>
                              <select
                                value={row.category}
                                onChange={(e) => handleUpdateRow(row.id, "category", e.target.value)}
                                style={{ width: "100%", padding: "0.5rem" }}
                              >
                                <option value="">Select category</option>
                                {categories.map((cat) => (
                                  <option key={cat.id} value={cat.name}>
                                    {cat.display_name || cat.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={row.description}
                                onChange={(e) => handleUpdateRow(row.id, "description", e.target.value)}
                                style={{ width: "100%", padding: "0.5rem" }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={row.totalAmount || ""}
                                onChange={(e) => handleUpdateRow(row.id, "totalAmount", e.target.value)}
                                style={{ width: "100%", padding: "0.5rem" }}
                                placeholder="Enter amount"
                              />
                            </td>
                            <td className="action-buttons">
                              <button
                                type="button"
                                className="icon-button delete"
                                onClick={() => handleDeleteRow(row.id)}
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                              <button
                                type="button"
                                className="icon-button copy"
                                onClick={() => handleCopyRow(row.id)}
                                title="Copy"
                              >
                                <FaCopy />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: "flex", gap: "1rem", marginTop: "1rem", alignItems: "flex-start", marginTop: "-10px" }}>
                    <button type="button" className="add-button" onClick={handleAddRow} title="Add Row">
                      Add Lines
                    </button>
                    <button type="button" className="add-secondary-button" onClick={handleClearAllLines} title="Clear Lines">
                      Clear all Lines
                    </button>
                  </div>
                  <div style={{ display: "flex", alignItems: "right", marginLeft: "1280px", marginTop: "-50px" }}>
                    <label style={{ fontSize: "12px", fontWeight: "bold" }}>Total Amount </label>
                    <p style={{ fontWeight: "bold", color: "#333", fontSize: "12px" }}>
                      : £{totalAmount.toFixed(2)}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", alignItems: "flex-start" }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: "-50px", marginTop: "30px" }}>
                      <label>Memo/Description</label>
                      <textarea
                        value={newBillData.memo}
                        onChange={(e) => setNewBillData({ ...newBillData, memo: e.target.value })}
                        placeholder="Enter memo or description"
                        style={{
                          width: "100%",
                          height: "100px",
                          padding: "0.7rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          resize: "vertical",
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ marginTop: "1rem" }}>
                      <label>Bill PDF {isEditing ? "(Optional)" : "(Required)"}</label>
                      <div
                        className="upload-area"
                        style={{
                          padding: "0.5rem",
                          height: "100px",
                          marginTop: "1px",
                          width: "100%",
                          textAlign: "center",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          marginTop: "15px"
                        }}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <p style={{ margin: "0", fontSize: "0.9rem" }}>
                          Click to select PDF file
                        </p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file && file.name.endsWith(".pdf")) {
                              setCreateBillFile(file);
                            } else {
                              alert("Please select a PDF file.");
                            }
                          }}
                          style={{ display: "none" }}
                        />
                        {createBillFile && (
                          <p
                            style={{
                              margin: "0.3rem 0",
                              color: "#333",
                              fontSize: "0.8rem",
                            }}
                          >
                            Selected: {createBillFile.name}
                          </p>
                        )}
                        {!createBillFile && (
                          <p
                            style={{
                              margin: "0.3rem 0",
                              color: "#666",
                              fontSize: "0.8rem",
                            }}
                          >
                            {isEditing ? "No new file selected" : "PDF file is required"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "center", marginTop: "1rem" }}>
                    <button
                      type="button"
                      style={{
                        padding: "0.7rem 1.5rem",
                        backgroundColor: "transparent",
                        color: "#004aad",
                        fontWeight: "bold",
                        marginTop: "-10px",
                        marginBottom: "-20px"
                      }}
                      onClick={() => alert("Privacy settings applied!")}
                    >
                      Privacy
                    </button>
                  </div>
                </div>
              </div>
              <div
                className="form-action-buttons"
                style={{
                  padding: "1rem",
                  backgroundColor: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderTop: "1px solid #ddd",
                  marginTop: "auto",
                }}
              >
                <div>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => {
                      setShowCreateBillPopup(false);
                      setIsEditing(false);
                      setSelectedBill(null);
                    }}
                    style={{ marginRight: "1rem" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="clear-btn"
                    onClick={handleClear}
                    style={{ marginRight: "0" }}
                  >
                    Clear
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="print-btn"
                    onClick={handlePrint}
                    style={{ marginRight: "1rem", borderRadius: "3px" }}
                  >
                    Print
                  </button>
                  <button
                    type="button"
                    className="recurring-btn"
                    onClick={handleRecurring}
                    style={{ marginRight: "0" }}
                  >
                    Recurring
                  </button>
                </div>
                <div>
                  <button
                    type="submit"
                    className="submit-btn"
                    style={{ marginRight: "1rem" }}
                    disabled={!isEditing && !createBillFile}
                  >
                    {isEditing ? "Save" : "Save"}
                  </button>
                  <button
                    type="button"
                    className="save-new-btn"
                    onClick={handleSaveAndNew}
                    style={{ marginRight: "0" }}
                    disabled={!isEditing && !createBillFile}
                  >
                    Save and New
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      {showEditBillPopup && selectedBill && (
        <div className="upload-popup-overlay">
          <div className="create-bill-popup" ref={editPopupRef}>
            <button
              className="close-button"
              onClick={() => {
                setShowEditBillPopup(false);
                setSelectedBill(null);
                setIsEditing(false);
              }}
            >
              ×
            </button>
            <h2 className="form-main-title">{isEditing ? "Edit Bill" : "Review Bill"}</h2>
            <div style={{ display: "flex", gap: "1rem" }}>
              <div style={{ flex: 1, padding: "1rem" }}>
                <h4>Attached PDF:</h4>
                {selectedBill.bill_pdf && normalizeAndValidateURL(selectedBill.bill_pdf) ? (
                  <div className="pdf-preview" style={{ border: "1px solid #ccc", borderRadius: "6px", overflow: "hidden" }}>
                    {console.log("PDF URL:", normalizeAndValidateURL(selectedBill.bill_pdf))}
                    <object
                      data={`${normalizeAndValidateURL(selectedBill.bill_pdf)}#toolbar=1&navpanes=1`}
                      type="application/pdf"
                      width="100%"
                      height="700px"
                      style={{ border: "none", display: "block", minHeight: "700px" }}
                    >
                      <p style={{ color: "red" }}>
                        Failed to load PDF. <a href={normalizeAndValidateURL(selectedBill.bill_pdf)} target="_blank" rel="noopener noreferrer">Open in new tab</a>.
                      </p>
                    </object>
                  </div>
                ) : (
                  <p>No valid PDF. Raw URL: {selectedBill.bill_pdf || 'Not provided'}</p>
                )}
              </div>
              <form id="edit-bill-form" onSubmit={handleEditBillSubmit} className="bill-form-container" style={{ flex: 1, padding: "1rem" }}>
                <div className="form-section">
                  <div style={{ backgroundColor: "#f0f0f0", padding: "10px" }}>
                    <h2 style={{ margin: 0 }}>Review Details</h2>
                    <p style={{ margin: 0 }}>Review the details before submitting</p>
                    <div className="form-group wide-group" style={{ flex: 1, marginTop: "30px" }}>
                      <label>Document type</label>
                      <select value="Bills" disabled style={{ backgroundColor: "#f0f0f0", width: "100%" }}>
                        <option value="Bills">Bills</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Payee</label>
                      <select
                        value={editBillData.supplier}
                        onChange={(e) => setEditBillData({ ...editBillData, supplier: e.target.value })}
                        disabled={!isEditing}
                      >
                        <option value="">Select Payee</option>
                        {suppliers.length === 0 ? (
                          <option disabled>No suppliers found 😵</option>
                        ) : (
                          suppliers.map((supplier) => (
                            <option key={supplier.id} value={supplier.id}>
                              {supplier.supplier_display_name || `${supplier.first_name || ''} ${supplier.last_name || ''}`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Bill Date</label>
                      <input
                        type="text"
                        placeholder="DD-MM-YYYY"
                        value={editBillData.date}
                        onChange={(e) => handleEditDateChange("date", e.target.value)}
                        style={{ width: "100%" }}
                        disabled={!isEditing}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Due Date</label>
                      <input
                        type="text"
                        placeholder="DD-MM-YYYY"
                        value={editBillData.dueDate}
                        onChange={(e) => handleEditDateChange("dueDate", e.target.value)}
                        style={{ width: "100%" }}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Total Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={editBillData.amount}
                        onChange={(e) => setEditBillData({ ...editBillData, amount: e.target.value })}
                        style={{ width: "100%" }}
                        placeholder="Amount in GBP"
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Description</label>
                      <input
                        type="text"
                        value={editBillData.address}
                        onChange={(e) => setEditBillData({ ...editBillData, address: e.target.value })}
                        style={{ width: "100%" }}
                        disabled={!isEditing}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Account/Category</label>
                      <select
                        value={editBillData.terms}
                        onChange={(e) => setEditBillData({ ...editBillData, terms: e.target.value })}
                        style={{ width: "100%" }}
                        disabled={!isEditing}
                      >
                        <option value="">Select category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.name}>
                            {cat.display_name || cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Bill No</label>
                      <input
                        type="text"
                        value={editBillData.billNumber}
                        onChange={(e) => setEditBillData({ ...editBillData, billNumber: e.target.value })}
                        style={{ width: "100%" }}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Memo</label>
                      <textarea
                        value={editBillData.memo}
                        onChange={(e) => setEditBillData({ ...editBillData, memo: e.target.value })}
                        placeholder="Enter memo or description"
                        style={{
                          width: "100%",
                          height: "100px",
                          padding: "0.7rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          resize: "vertical",
                        }}
                        disabled={!isEditing}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div
              className="form-action-buttons"
              style={{
                padding: "1rem",
                backgroundColor: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: "1px solid #ddd",
                marginTop: "auto",
              }}
            >
              <div>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowEditBillPopup(false);
                    setSelectedBill(null);
                    setIsEditing(false);
                  }}
                  style={{ marginRight: "1rem" }}
                >
                  Cancel
                </button>
              </div>
              {isEditing && (
                <div>
                  <button
                    type="submit"
                    form="edit-bill-form"
                    className="submit-btn"
                    style={{ marginRight: "1rem" }}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirmation && (
        <div className="delete-confirmation-overlay">
          <div className="delete-confirmation-modal">
            <h2>Confirm Deletion</h2>
            <p>
              {billToDelete
                ? 'Are you sure you want to delete this bill? This action cannot be undone.'
                : `Are you sure you want to delete ${selectedBills.size} selected bill${selectedBills.size > 1 ? 's' : ''}? This action cannot be undone.`}
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
              <button className="confirm-btn" onClick={handleConfirmDelete}>Yes, Delete</button>
              <button className="cancel-btn" onClick={handleCancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <div className="billss-table">
        <div className="tables-row tables-header">
          <div className="checkbox-col">
            <input
              type="checkbox"
              onChange={handleSelectAll}
              checked={bills.length > 0 && selectedBills.size === bills.length}
            />
          </div>
          <div className="bill-col">Bill</div>
          <div className="source-col">Source</div>
          <div className="supplier-col">Supplier</div>
          <div className="bill-no-col">Bill No</div>
          <div className="bill-date-col">Bill Date</div>
          <div className="category-col">Category</div>
          <div className="due-date-col">Due Date</div>
          <div className="bill-amount-col">Bill Amount</div>
          <div className="action-col">Action</div>
        </div>
        {filteredBills.length === 0 && (
          <div className="no-bills-message">
            <h3>{searchTerm ? "No bills match your search" : "No bills to review"}</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms or clear the search to see all bills."
                : "When you have bills ready to review, you'll find them here. To upload a bill or create a new one, go to Add Bill."}
            </p>
          </div>
        )}
        {filteredBills.map((bill) => (
          <div
            key={bill.id}
            className="table-row"
            onClick={(e) => {
              if (!e.target.closest(".action-col") && !e.target.closest("input[type='checkbox']")) {
                handleViewBill(bill);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="checkbox-col">
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={filteredBills.length > 0 && selectedBills.size === filteredBills.length}
              />
            </div>
            <div className="bill-col">
              <TbReceipt style={{ fontSize: "1.5rem", color: "#202020ff" }} title={bill.fileName} />
            </div>
            <div className="source-col">{bill.fileURL ? "Uploaded" : "Manual"}</div>
            <div className="supplier-col">{getSupplierName(bill.supplier)}</div>
            <div className="bill-no-col">{bill.billNumber}</div>
            <div className="bill-date-col">{bill.date}</div>
            <div className="category-col">{bill.category}</div>
            <div className="due-date-col">{bill.dueDate}</div>
            <div className="bill-amount-col">{bill.amount}</div>
            <div className="action-col" style={{ display: "flex", gap: "0.5rem" }}>
              <button className="icon-button view" onClick={() => handleView(bill)} title="View"><FaEye /></button>
              <button className="icon-button edit" onClick={() => handleEdit(bill)} title="Edit"><FaEdit /></button>
              <button
                className="icon-button delete"
                onClick={() => {
                  setBillToDelete(bill.id);
                  setShowDeleteConfirmation(true);
                }}
                title="Delete"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BillsPage;